import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';

import { environment } from '../../environments/environment';

import { HttpErrorHandler, HandleError } from './http-error-handler.service';

import {
  OpenNotesPaymentInfoListItem,
  OpenNotesPaymentInfoListGroupFooter,
  PaymentAddress,
  PaymentDetail,
  AutoPaymentDetail,
  PaymentConfirmationDialogData,
  AutoPaymentConfirmationDialogData,
  OpenNotesPaymentHistoryListItem,
  NotePaymentHistoryDetailsListItem,
  NoteBalanceAdjustmentsListItem,
  NoteTotalAmountPaidItem,
  AutoPayNotesListItem,
  NoteAutoPayInformation,
  ACHPaymentInformation,
  CreditCardPaymentInformation,
  AutoPayACHPaymentInformation,
  AutoPayCreditCardPaymentInformation,
  CancelRecurringPaymentInformation,
  BrainTreeClientToken,
  BraintreeCustomerCreditCardInfo,
  BraintreeCreditCardPaymentReturn,
  PendingPayment
} from '../models/payment';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  private _logging = environment.logging;
  private _apiSettings = environment.restAPI;
  private _baseServiceUrl = '';
  private _handleError: HandleError;

  constructor(@Inject('BASE_URL') baseUrl: string, private _httpClient: HttpClient, httpErrorHandler: HttpErrorHandler) {
    this._baseServiceUrl = (this._apiSettings.useStaticUrl ? this._apiSettings.staticUrl : baseUrl) + this._apiSettings.urlSuffix;
    this._handleError = httpErrorHandler.createHandleError('PaymentService');
  }

  public getOpenNotesPaymentInfoList(participantId: number, noteId: number = null): Observable<OpenNotesPaymentInfoListItem[]> {
    const serviceUrl: string =
      this._baseServiceUrl + 'Payment/OpenNotesPaymentInfo';
    if (this._logging.serviceCallTraceLog) {
      console.log('PaymentService.getOpenNotesPaymentInfoList :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<Array<OpenNotesPaymentInfoListItem>>(serviceUrl, {participantId: participantId, noteId: noteId}).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getOpenNotesPaymentInfoList', []))
    );
  }

  public getPendingPayment(participantId: number, noteId: number = null): Observable<PendingPayment> {
    const serviceUrl: string = this._baseServiceUrl + 'Payment/PendingPayment';

    if (this._logging.serviceCallTraceLog) {
      console.log('PaymentService.getPendingPayment :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<PendingPayment>(serviceUrl, {participantId: participantId, noteId: noteId}).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getPendingPayment', null))
    );
  }

  public addOpenNotesPaymentInfoListFooters(
    openNotesPaymentInfoList: OpenNotesPaymentInfoListItem[]
  ): (OpenNotesPaymentInfoListItem | OpenNotesPaymentInfoListGroupFooter)[] {
    const updatedOpenNotesPaymentInfoList: (OpenNotesPaymentInfoListItem | OpenNotesPaymentInfoListGroupFooter)[] = [];

    let currentPaymentGroup: string = null;
    let pastDueAmountTotal = 0;
    let lateFeeDueTotal = 0;
    let regularPaymentAmountTotal = 0;
    let totalAmountDueTotal = 0;
    let payoffAmountTotal = 0;
    let autoPayFlagTotal = 'N';

    for (const openNotesPaymentInfoListItem of openNotesPaymentInfoList) {
      if (openNotesPaymentInfoListItem.paymentGroup !== currentPaymentGroup) {
        if (currentPaymentGroup !== null) {
          updatedOpenNotesPaymentInfoList.push(
            this._getNewOpenNotesPaymentInfoListGroupFooter(
              currentPaymentGroup,
              pastDueAmountTotal,
              lateFeeDueTotal,
              regularPaymentAmountTotal,
              totalAmountDueTotal,
              payoffAmountTotal,
              autoPayFlagTotal
            )
          );
        }
        currentPaymentGroup = openNotesPaymentInfoListItem.paymentGroup;
        pastDueAmountTotal = 0;
        lateFeeDueTotal = 0;
        regularPaymentAmountTotal = 0;
        totalAmountDueTotal = 0;
        payoffAmountTotal = 0;
        autoPayFlagTotal = 'N';
      }
      updatedOpenNotesPaymentInfoList.push(openNotesPaymentInfoListItem);

      pastDueAmountTotal += openNotesPaymentInfoListItem.pastDueAmount;
      lateFeeDueTotal += openNotesPaymentInfoListItem.lateFeeDue;
      regularPaymentAmountTotal += openNotesPaymentInfoListItem.regularPaymentAmount;
      totalAmountDueTotal += openNotesPaymentInfoListItem.totalAmountDue;
      payoffAmountTotal += openNotesPaymentInfoListItem.payoffAmount;
      if (openNotesPaymentInfoListItem.autoPayFlag !== null && openNotesPaymentInfoListItem.autoPayFlag.toUpperCase() === 'Y') {
        autoPayFlagTotal = 'Y';
      }
    }

    if (currentPaymentGroup !== null) {
      updatedOpenNotesPaymentInfoList.push(
        this._getNewOpenNotesPaymentInfoListGroupFooter(
          currentPaymentGroup,
          pastDueAmountTotal,
          lateFeeDueTotal,
          regularPaymentAmountTotal,
          totalAmountDueTotal,
          payoffAmountTotal,
          autoPayFlagTotal
        )
      );
    }

    return updatedOpenNotesPaymentInfoList;
  }

  private _getNewOpenNotesPaymentInfoListGroupFooter(
    paymentGroup: string,
    pastDueAmountTotal: number,
    lateFeeDueTotal: number,
    regularPaymentAmountTotal: number,
    totalAmountDueTotal: number,
    payoffAmountTotal: number,
    autoPayFlagTotal: string
  ): OpenNotesPaymentInfoListGroupFooter {
    let openNotesPaymentInfoListGroupFooter: OpenNotesPaymentInfoListGroupFooter;
    openNotesPaymentInfoListGroupFooter = {
      isGroupFooter: true,
      paymentGroup: paymentGroup,
      pastDueAmountTotal: pastDueAmountTotal,
      lateFeeDueTotal: lateFeeDueTotal,
      regularPaymentAmountTotal: regularPaymentAmountTotal,
      totalAmountDueTotal: totalAmountDueTotal,
      payoffAmountTotal: payoffAmountTotal,
      autoPayFlagTotal: autoPayFlagTotal
    };
    return openNotesPaymentInfoListGroupFooter;
  }

  public getPaymentAddress(participantId: number, noteIds: number[] = []): Observable<PaymentAddress> {
    const serviceUrl: string =
      this._baseServiceUrl + 'Payment/PaymentAddress';
    if (this._logging.serviceCallTraceLog) {
      console.log('PaymentService.getPaymentAddress :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<PaymentAddress>(serviceUrl, {participantId: participantId,
      noteIds: (noteIds !== null && noteIds.length > 0 ? noteIds.join() : '')}).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getPaymentAddress', null))
    );
  }

  public getNewPaymentDetail(): PaymentDetail {
    const paymentDetail: PaymentDetail = {
      participantId: null,
      noteIds: null,
      name: null,
      addressLine1: null,
      addressLine2: null,
      city: null,
      state: null,
      zipCode: null,
      paymentAmount: null,
      paymentDueDay: null,
      loanProgram: null,
      servicingCompany: null,
      paymentType: null,
      bankRoutingNumber: null,
      bankAccountNumber: null,
      bankAccountConfirmNumber: null,
      bankAccountType: null,
      userName: null,
      paymentRef: null,
      checkPaymentId: null,
      confirmationCode: null,
      responseCode: null,
      responseMessage: null,

      /* Braintree tokenized payload */
      nonce: null,
      partiFirstName: null,
      partiLastName: null,
      partiEmail: null,
      deviceData: null,
      creditCardBin: null,
      creditCardType: null,
      lastFourCreditCard: null,
      creditCardExpMonth: null,
      creditCardExpYear: null,
      brainTreePaymentType: null,
      brainTreeLenderProgramCode: null,
    };
    return paymentDetail;
  }

  public getNewPaymentConfirmationDialogData(): PaymentConfirmationDialogData {
    const paymentConfirmationDialogData: PaymentConfirmationDialogData = {
      dialogTitle: null,
      paymentOk: false,
      paymentError: null,
      paymentDetail: this.getNewPaymentDetail()
    };
    return paymentConfirmationDialogData;
  }

  public getOpenNotesPaymentHistoryList(participantId: number): Observable<OpenNotesPaymentHistoryListItem[]> {
    const serviceUrl: string = this._baseServiceUrl + 'Payment/OpenNotesPaymentHistory';
    if (this._logging.serviceCallTraceLog) {
      console.log('PaymentService.getOpenNotesPaymentHistoryList :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<Array<OpenNotesPaymentHistoryListItem>>(serviceUrl, {participantId: participantId}).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getOpenNotesPaymentHistoryList', []))
    );
  }

  public getNotePaymentHistoryDetailsList(noteId: number): Observable<NotePaymentHistoryDetailsListItem[]> {
    const serviceUrl: string =
      this._baseServiceUrl + 'Payment/NotePaymentHistoryDetails';
    if (this._logging.serviceCallTraceLog) {
      console.log('PaymentService.GetNotePaymentHistoryDetailsList :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<Array<NotePaymentHistoryDetailsListItem>>(serviceUrl, {noteId: noteId}).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('GetNotePaymentHistoryDetailsList', []))
    );
  }

  public getNoteBalanceAdjustmentsList(participantId: number, noteId: number): Observable<NoteBalanceAdjustmentsListItem[]> {
    const serviceUrl: string =
      this._baseServiceUrl + 'Payment/NoteBalanceAdjustments';
    if (this._logging.serviceCallTraceLog) {
      console.log('PaymentService.GetNoteBalanceAdjustmentsList :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<Array<NoteBalanceAdjustmentsListItem>>(serviceUrl, {participantId: participantId, noteId: noteId}).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('GetNoteBalanceAdjustmentsList', []))
    );
  }

  public getNoteTotalAmountPaid(noteId: number, participantId: number): Observable<NoteTotalAmountPaidItem[]> {
    const serviceUrl: string =
      this._baseServiceUrl + 'Payment/NoteTotalAmountPaid';
    if (this._logging.serviceCallTraceLog) {
      console.log('PaymentService.GetNoteTotalAmountPaid :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<Array<NoteTotalAmountPaidItem>>(serviceUrl, {noteId: noteId, participantId: participantId}).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('GetNoteTotalAmountPaid', []))
    );
  }

  public getAutoPayNotesList(participantId: number): Observable<AutoPayNotesListItem[]> {
    const serviceUrl: string = this._baseServiceUrl + 'Payment/AutoPayNotes';
    if (this._logging.serviceCallTraceLog) {
      console.log('PaymentService.getAutoPayNotesList :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<Array<AutoPayNotesListItem>>(serviceUrl, {participantId: participantId}).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getAutoPayNotesList', []))
    );
  }

  public getNoteAutoPayInformation(participantId: number, noteId: number): Observable<NoteAutoPayInformation> {
    const serviceUrl: string =
      this._baseServiceUrl + 'Payment/NoteAutoPayInformation';
    if (this._logging.serviceCallTraceLog) {
      console.log('PaymentService.getNoteAutoPayInformation :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<NoteAutoPayInformation>(serviceUrl, {participantId: participantId, noteId: noteId}).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getNoteAutoPayInformation', null))
    );
  }

  public getNewAutoPaymentDetail(): AutoPaymentDetail {
    const autoPaymentDetail: AutoPaymentDetail = {
      participantId: null,
      noteId: null,
      name: null,
      addressLine1: null,
      addressLine2: null,
      city: null,
      state: null,
      zipCode: null,
      paymentAmount: null,
      paymentDueDay: null,
      loanProgram: null,
      servicingCompany: null,
      paymentType: null,
      bankRoutingNumber: null,
      bankAccountNumber: null,
      bankAccountConfirmNumber: null,
      bankAccountType: null,
      userName: null,
      paymentRef: null,
      confirmationCode: null,
      responseCode: null,
      responseMessage: null,
      confirmationNumbers: null,
      firstPaymentAmount: null,
      firstPaymentDate: null,
      isAutoPaymentSetup: false,
      isAutoPaymentCancel: false,

       /* Braintree tokenized payload */
       nonce: null,
       partiFirstName: null,
       partiLastName: null,
       partiEmail: null,
       deviceData: null,
       creditCardBin: null,
       creditCardType: null,
       lastFourCreditCard: null,
       creditCardExpMonth: null,
       creditCardExpYear: null,
       brainTreePaymentType: null,
       brainTreeLenderProgramCode: null,
    };
    return autoPaymentDetail;
  }

  public getNewAutoPaymentConfirmationDialogData(): AutoPaymentConfirmationDialogData {
    const autoPaymentConfirmationDialogData: AutoPaymentConfirmationDialogData = {
      dialogTitle: null,
      paymentOk: false,
      paymentError: null,
      autoPaymentDetail: this.getNewAutoPaymentDetail()
    };
    return autoPaymentConfirmationDialogData;
  }

  public setupACHPayment(achPaymentInformation: ACHPaymentInformation): Observable<number> {
    const serviceUrl: string = this._baseServiceUrl + 'Payment/SetupACHPayment';

    if (this._logging.serviceCallTraceLog) {
      console.log('PaymentService.setupACHPayment :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<number>(serviceUrl, achPaymentInformation).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('setupACHPayment', null))
    );
  }

  public setupCreditCardPayment(creditCardPaymentInformation: CreditCardPaymentInformation): Observable<BraintreeCreditCardPaymentReturn> {
    const serviceUrl: string = this._baseServiceUrl + 'Payment/SetupCreditCardPayment';

    if (this._logging.serviceCallTraceLog) {
      console.log('PaymentService.setupCreditCardPayment :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<BraintreeCreditCardPaymentReturn>(serviceUrl, creditCardPaymentInformation).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('setupCreditCardPayment', null))
    );
  }

  public setupPayPalPayment(creditCardPaymentInformation: CreditCardPaymentInformation): Observable<BraintreeCreditCardPaymentReturn> {
    const serviceUrl: string = this._baseServiceUrl + 'Payment/SetupPayPalPayment';

    if (this._logging.serviceCallTraceLog) {
      console.log('PaymentService.setupPayPalPayment :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<BraintreeCreditCardPaymentReturn>(serviceUrl, creditCardPaymentInformation).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('setupPayPalPayment', null))
    );
  }

  public setupGooglePayPayment(creditCardPaymentInformation: CreditCardPaymentInformation): Observable<BraintreeCreditCardPaymentReturn> {
    const serviceUrl: string = this._baseServiceUrl + 'Payment/SetupGooglePayPayment';

    if (this._logging.serviceCallTraceLog) {
      console.log('PaymentService.setupGooglePayPayment :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<BraintreeCreditCardPaymentReturn>(serviceUrl, creditCardPaymentInformation).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('setupGooglePayPayment', null))
    );
  }

  public setupApplePayPayment(creditCardPaymentInformation: CreditCardPaymentInformation): Observable<BraintreeCreditCardPaymentReturn> {
    const serviceUrl: string = this._baseServiceUrl + 'Payment/SetupApplePayPayment';

    if (this._logging.serviceCallTraceLog) {
      console.log('PaymentService.setupApplePayPayment :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<BraintreeCreditCardPaymentReturn>(serviceUrl, creditCardPaymentInformation).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('setupApplePayPayment', null))
    );
  }

  public setupAutoPayACHPayment(autoPayACHPaymentInformation: AutoPayACHPaymentInformation): Observable<string> {
    const serviceUrl: string = this._baseServiceUrl + 'Payment/SetupAutoPayACHPayment';

    if (this._logging.serviceCallTraceLog) {
      console.log('PaymentService.setupAutoPayACHPayment :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<string>(serviceUrl, autoPayACHPaymentInformation).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('setupAutoPayACHPayment', null))
    );
  }

  public setupAutoPayCreditCardPayment(autoPayCreditCardPaymentInformation: AutoPayCreditCardPaymentInformation): Observable<number> {
    const serviceUrl: string = this._baseServiceUrl + 'Payment/SetupAutoPayCreditCardPayment';

    if (this._logging.serviceCallTraceLog) {
      console.log('PaymentService.setupAutoPayCreditCardPayment :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<number>(serviceUrl, autoPayCreditCardPaymentInformation).pipe(
      retry(this._apiSettings.retryCount),               //  02/05/2021 RA - Retry count for processing payments
      catchError(this._handleError('setupAutoPayCreditCardPayment', null))
    );
  }

  public cancelAutoPay(cancelRecurringPaymentInformation: CancelRecurringPaymentInformation): Observable<number> {
    const serviceUrl: string = this._baseServiceUrl + 'Payment/CancelAutoPay';

    if (this._logging.serviceCallTraceLog) {
      console.log('PaymentService.cancelAutoPay :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<number>(serviceUrl, cancelRecurringPaymentInformation).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('cancelAutoPay', null))
    );
  }

  /**
   * Routine to obtain the Client token from the server
   *
   * @param loanProgramCode - Loan program used to obtain proper credentials
   *
   * @returns Client token used to communicate to Braintree
   */
  public getBrainTreeClientToken(loanProgramCode: string) {
    const serviceUrl: string = this._baseServiceUrl + 'Payment/GetBrainTreeClientToken';

    if (this._logging.serviceCallTraceLog) {
      console.log('PaymentService.getBrainTreeClientToken :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<BrainTreeClientToken>(serviceUrl, {loanProgramCode : loanProgramCode}).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('getBrainTreeClientToken', null))
    );
  }

  /**
   * Routine to obtain the default Credit Card information for a customer; Only exposed information permitted.
   *
   * @param loanProgramCode - Loan program used to obtain proper credentials
   * @param participantId - Participant id
   * @param noteId - Note Id
   * @param isAutoPay - Is Autopay  (true) or Single Payment (false)
   *
   * @returns Default credit card information
   */
  public GetBraintreeDefaultCreditCardInfo(loanProgramCode: string, participantId: number, noteId: string, isAutoPay: boolean) {
    const serviceUrl: string = this._baseServiceUrl + 'Payment/GetDefaultCreditCardInfo';

    if (this._logging.serviceCallTraceLog) {
      console.log('PaymentService.GetBraintreeDefaultCreditCardInfo :: serviceUrl = ' + serviceUrl);
    }

    return this._httpClient.post<BraintreeCustomerCreditCardInfo>(serviceUrl, {loanProgramCode : loanProgramCode,
                                                                               participantId: participantId,
                                                                               noteId: noteId,
                                                                               isAutoPay: isAutoPay}).pipe(
      retry(this._apiSettings.retryCount),
      catchError(this._handleError('GetBraintreeDefaultCreditCardInfo', null))
    );

  }
}
